import React from 'react';

import { BoxProps, Flex, Group, Paper, Text, Tooltip, useMantineTheme } from '@mantine/core';
import { IconCheck, IconLock, IconPlus } from '@tabler/icons-react';
import { DroxyBadge } from 'components/Droxy/DroxyBadge';
import { DroxyBoxedIcon } from 'components/Droxy/DroxyBoxedIcon';
import { useTranslation } from 'react-i18next';
import { StaticToolGroup } from 'shared/interfaces/tools/Tool';

interface StaticToolGroupCardProps extends BoxProps {
  staticToolGroup: StaticToolGroup;
  isAvailable: boolean;
  isAttached: boolean;
  onClick?: () => void;
}

export function StaticToolGroupCard({
  staticToolGroup,
  onClick,
  isAvailable,
  isAttached,
  ...boxProps
}: StaticToolGroupCardProps) {
  const { t } = useTranslation();
  const theme = useMantineTheme();
  const isClickable = !!onClick;

  const getStatusIcon = () => {
    if (isAttached) {
      return <IconCheck size={18} style={{ color: theme.colors.green[6] }} />;
    }

    if (!isAvailable) {
      return (
        <Tooltip label={t('SMART_ASSISTANT.TOOLS_SECTION.UNAVAILABLE_TOOL_TOOLTIP')}>
          <Group spacing="xs" noWrap>
            <DroxyBadge>{staticToolGroup.minProduct}</DroxyBadge>
            <IconLock size={16} style={{ color: theme.colors.gray[6] }} />
          </Group>
        </Tooltip>
      );
    }

    return <IconPlus size={18} style={{ color: theme.colors.gray[6] }} />;
  };

  return (
    <Paper
      onClick={onClick}
      p="md"
      h={130}
      sx={(theme) => ({
        border: `1px solid ${theme.colors.gray[2]}`,
        cursor: isClickable ? 'pointer' : 'default',
        '&:hover': isClickable
          ? {
              backgroundColor: theme.colors.gray[0]
            }
          : {}
      })}
      {...boxProps}>
      <Flex direction="column" h="100%">
        <Group position="apart" mb="sm" noWrap>
          <DroxyBoxedIcon icon={staticToolGroup.icon} hasBorder size={32} />
          {getStatusIcon()}
        </Group>

        <Text size="sm" fw={600} mb={4} lineClamp={1}>
          {staticToolGroup.displayName}
        </Text>

        <Text size="xs" c="dimmed" lineClamp={2}>
          {staticToolGroup.displayDescription}
        </Text>
      </Flex>
    </Paper>
  );
}
